import { Maximize, Minimize } from "lucide-react";
import React, { useCallback } from "react";
import { WebViewTooltip } from "../../Pages/Diary/SubHeader/WebViewTooltip";
import { cn } from "../../../lib/utils";

const tryUpdateIframesInWindow = (
  parentWindow: Window,
  currentWindow: Window,
  setFullscreenEnabled: (enabled: boolean) => void
): boolean => {
  const iframes = parentWindow.document.querySelectorAll("iframe");
  for (const iframe of iframes) {
    try {
      if (iframe.contentWindow !== currentWindow) continue;

      const currentAllow = iframe.getAttribute("allow") ?? "";
      if (!currentAllow.includes("fullscreen")) {
        const newAllow = currentAllow
          ? `${currentAllow}; fullscreen`
          : "fullscreen";
        iframe.setAttribute("allow", newAllow);
        setTimeout(() => setFullscreenEnabled(document.fullscreenEnabled), 100);
      }
      return true;
    } catch {
      continue;
    }
  }
  return false;
};

const findAndUpdateIframe = (
  setFullscreenEnabled: (enabled: boolean) => void
): void => {
  let currentWindow: Window = window;
  let parentWindow = window.parent;

  while (parentWindow && parentWindow !== currentWindow) {
    try {
      if (
        tryUpdateIframesInWindow(
          parentWindow,
          currentWindow,
          setFullscreenEnabled
        )
      ) {
        return;
      }
      currentWindow = parentWindow;
      parentWindow = parentWindow.parent;
    } catch {
      break;
    }
  }
};

const enableFullscreenInParentIframe = (
  setFullscreenEnabled: (enabled: boolean) => void
): void => {
  if (document.fullscreenEnabled) {
    setFullscreenEnabled(true);
    return;
  }

  if (window.self === window.top) return;

  try {
    findAndUpdateIframe(setFullscreenEnabled);
  } catch (e) {
    console.warn("Could not access parent iframe to enable fullscreen:", e);
  }
};

const FullScreen = () => {
  const [isFullscreen, setIsFullscreen] = React.useState(
    !!document.fullscreenElement
  );
  const [fullscreenEnabled, setFullscreenEnabled] = React.useState(
    document.fullscreenEnabled
  );

  // Effect to handle iframe fullscreen attribute setup
  React.useEffect(() => {
    enableFullscreenInParentIframe(setFullscreenEnabled);
  }, []);

  const onFullscreenChange = useCallback(() => {
    if (isFullscreen) {
      // browser is fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        /* Safari */
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        /* IE11 */
        (document as any).msExitFullscreen();
      }
      setIsFullscreen(false);
    } else {
      const element: any = document.getElementById("root");
      if (element?.requestFullscreen) {
        element.requestFullscreen();
      } else if (element?.webkitRequestFullscreen) {
        /* Safari */
        element.webkitRequestFullscreen();
      } else if (element?.msRequestFullscreen) {
        /* IE11 */
        element.msRequestFullscreen();
      }
      setIsFullscreen(true);
    }
  }, [isFullscreen]);

  if (!fullscreenEnabled) {
    return null;
  }

  return (
    <WebViewTooltip
      content={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
      side="bottom"
      sideOffset={8}
    >
      <button
        className={cn("eds-ghost-icon-button", isFullscreen && "active")}
        onClick={onFullscreenChange}
        aria-label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
      >
        {isFullscreen ? <Minimize /> : <Maximize />}
      </button>
    </WebViewTooltip>
  );
};

export default FullScreen;
